import dayjs from "dayjs";
import { Dropdown, Form, Menu, Modal, Pagination, Table, Tag, Tooltip } from "antd";
import { CaretDownOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faRoute } from "@fortawesome/free-solid-svg-icons";
import TextArea from "antd/es/input/TextArea";

import Spinner from "@src/components/spinner";
import { imageUrlToBase64 } from "@src/util/base64";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getScheduleCheckCallQuery, updateScheduleCheckCallMutation } from "@src/util/mutation/scheduleChecks.mutation";
import { deleteAssignedTaskOnSchedule } from "@src/util/mutation/scheduleTask.mutation";
import { AssignNewScheduleTaskBulkMutation } from "@src/util/mutation/scheduleTaskAssignment.mutation";
import { AddNewBulkTaskintoCheckCallMutation } from "@src/util/mutation/siteCheckCall.mutation";
import { PatrolReportDocument } from "@src/components/checkCallReportActivity/checkCallReportActivity";
import { SiteDutyTaskModal } from "@src/pages/timeline/siteDutyTask.modal";
import { TaskAssignmentModal } from "@src/pages/schedule/addDuty/taskAssignment.modal";
import { TaskAssignmentModalGeneric } from "@src/pages/schedule/addDuty/taskAssignment.modal.generic";
import { RouteMapModal } from "@src/components/routeMap/routeMap.modal";
import { SiteGeoLocationQuery } from "@src/util/query/siteGeoLocation.query";
import { EditDutyReportTab } from "./editDutyReportTab";
import { downloadFullDutyReport } from "@src/components/proofOfAttendance/proofOfAttendanceDrawer";

// Calls, Task and Checkpoint used to be three separate tabs, each with its own table -
// merged here into one "Activity" table (a Type tag distinguishes the rows now that
// they're mixed together), plus a "See Activity on Map" link replacing what used to be
// a separate Route tab. All of the original per-type behaviour (comment on a check call,
// add/delete a task, click a task/checkpoint for its detail, PDF guarding report) is
// still here, just reading off one shared dataset instead of three separate queries.

const TYPE_STYLES = {
    "Clock In": { bg: "#e0f2fe", color: "#0369a1" },
    "Clock Out": { bg: "#e0f2fe", color: "#0369a1" },
    "Check call": { bg: "#d1fae5", color: "#065f46" },
    "Task": { bg: "#ede9fe", color: "#6d28d9" },
    "Checkpoint": { bg: "#ffedd5", color: "#c2410c" },
};
const DEFAULT_TYPE_STYLE = { bg: "#f3f4f6", color: "#374151" };

const getPriorityReverse = (priority) => {
    switch (priority) {
        case 0: return "Normal";
        case 1: return "Medium";
        case 2: return "High";
        default: return "--";
    }
};

// Same "was this actually completed live from the app, with a real GPS fix" rule the
// old standalone Route tab used - folded in here since Activity now owns the "See
// Activity on Map" link/modal instead of the map having its own tab.
const isMappable = (call) =>
    call?.submittedVia === "WFC App" &&
    !call?.pending &&
    Number.isFinite(call?.actualLocation?.latitude) &&
    Number.isFinite(call?.actualLocation?.longitude);

export const EditDutyActivityTab = ({ scheduleId, siteId, sitegroupId, addTask = false, shiftStartTime, shiftEndTime, isCompleted }) => {

    const { data: scheduleCheckCall, loading: scheduleCheckCallLoading, refetch } = useQuery(getScheduleCheckCallQuery, {
        variables: { siteId, scheduleId },
        fetchPolicy: "network-only",
        skip: !siteId || !scheduleId
    });

    const calls = scheduleCheckCall?.getScheduleCheckCalls?.response || [];
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ---------------- Route ----------------
    const [routeModalOpen, setRouteModalOpen] = useState(false);
    const mappableCalls = calls
        .filter(isMappable)
        .sort((a, b) => new Date(a?.actualDate).getTime() - new Date(b?.actualDate).getTime());
    const routeCoords = mappableCalls.map((call) => `${call.actualLocation.latitude},${call.actualLocation.longitude}`);
    const routeTitles = mappableCalls.map((call) => {
        const time = call?.actualDate ? dayjs.utc(call.actualDate).tz("Europe/London").format("HH:mm") : "--";
        return `${call?.label || call?.callType} - ${time}`;
    });

    // Site's own geofence, overlaid on "See Activity on Map" as a plain shape (no pin) -
    // same radius/path Site Geofencing (siteGeofencing.jsx) manages, fetched the same way.
    // Center comes off siteDetail.location (already joined onto every call by this tab's
    // main query) rather than re-geocoding the address - it's the exact point saved via
    // the address-autocomplete widget when the site was set up (see addressLookups.jsx),
    // stored as {northeast, southwest} both equal to that one resolved point.
    const { data: siteGeoData } = useQuery(SiteGeoLocationQuery, {
        variables: { siteId },
        skip: !siteId
    });
    const siteLocation = calls?.[0]?.siteDetail?.location;
    const siteGeoPoint = siteLocation?.northeast || siteLocation?.southwest;
    const siteGeoCenter = (siteGeoPoint?.lat != null && siteGeoPoint?.lng != null)
        ? { lat: +siteGeoPoint.lat, lng: +siteGeoPoint.lng }
        : null;
    const siteGeoRadius = Number(siteGeoData?.getSiteGeoLocation?.response?.radius) || 0;
    const siteGeoPath = siteGeoData?.getSiteGeoLocation?.response?.path;
    const siteGeofence = siteGeoCenter && (siteGeoRadius > 0 || siteGeoPath?.length > 2)
        ? { center: siteGeoCenter, radius: siteGeoRadius, path: siteGeoPath }
        : null;

    // ---------------- PDF guarding report (Check call / Clock In-Out rows only, same scope as before) ----------------
    const [scheduleCallData, setScheduleCallData] = useState([]);
    useEffect(() => {
        const fetchImages = async () => {
            if (!scheduleCheckCall?.getScheduleCheckCalls?.response || scheduleCheckCallLoading) return;
            const reportable = scheduleCheckCall.getScheduleCheckCalls.response.filter(
                (call) => call?.callType !== "Task" && call?.callType !== "Checkpoint"
            );
            const withImages = await Promise.all(reportable.map(async (call) => ({
                ...call,
                checkCallActivityImage: call?.checkCallActivityImage?.length > 3 ? await imageUrlToBase64(call.checkCallActivityImage) : null,
            })));
            setScheduleCallData(withImages);
        };
        fetchImages();
    }, [scheduleCheckCall?.getScheduleCheckCalls?.response, scheduleCheckCallLoading]);

    const checkCallReportEnabled = calls?.[0]?.sitecheckcallsDetail?.checkCallReport;

    // ---------------- Comment (Check call rows) ----------------
    const [commentModal, setCommentModal] = useState({});

    // ---------------- Image preview (Clock In/Out selfie + Check call image) ----------------
    // Same transparent-Modal lightbox Key Management uses for its "Key Photos" section
    // (keOverview.jsx) - a bare <img> plus Prev/Next, not antd's own Image preview chrome.
    // Index is into `activityImages` below (built in table row order), not tied to which
    // page of the table is currently showing, so Prev/Next can step across pages.
    const [imagePreviewIndex, setImagePreviewIndex] = useState(null);

    // ---------------- Detail view (Task/Checkpoint rows) ----------------
    const [siteDutyTaskModal, setSiteDutyTaskModal] = useState(false);
    const [formDetail, setFormDetail] = useState({});

    const openDetail = (checkCall) => {
        (checkCall?.tasklibraryDetail?.flow === "formBase" || checkCall?.sitecheckpointDetail?.formId) ?
            setFormDetail({
                modal: true,
                templateId: checkCall?.tasklibraryDetail?.formId || checkCall?.sitecheckpointDetail?.formId,
                reportCallTypeId: checkCall?._id,
                editData: {
                    jobId: checkCall?.scheduleId,
                    sitename: null,
                    _id: checkCall?.siteId,
                    selectedarea: { _id: null },
                    customerDetail: { _id: checkCall?.customerId },
                },
                jobData: {
                    site: { _id: checkCall?.siteId },
                    _id: checkCall?.scheduleId,
                },
                title: checkCall?.label,
                call: checkCall,
            })
            :
            setSiteDutyTaskModal({ modal: true, siteTaskId: checkCall?.taskId || checkCall?.scheduleAssignedTaskId, call: checkCall });
    };

    // ---------------- Delete (pending Task/Checkpoint rows) ----------------
    const [deleteAssignedTask, { loading: deleteAssignedTaskLoading }] = useMutation(deleteAssignedTaskOnSchedule);
    const handelDeleteTask = async (taskId, callId, scheduleAssignedTaskId) => {
        try {
            await deleteAssignedTask({
                variables: { checkCallId: callId, scheduleId, scheduleAssignedTaskId }
            });
            await refetch();
            dispatch(setNotification({ notificationState: true, error: false, message: "Deleted successfully" }));
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err?.message }));
        }
    };

    const confirmDelete = (checkCall) => {
        Swal.fire({
            icon: "warning",
            title: `Delete ${checkCall?.callType}?`,
            html: `
                <div style="text-align: left; padding: 10px;">
                    <p style="margin-bottom: 10px;">
                        Do you really want to <strong>delete this ${checkCall?.callType?.toLowerCase()}</strong>?
                    </p>
                    <p style="color: #d33; font-weight: 500;">
                        This action <strong>cannot be undone</strong> and it will also be removed from the Officer App.
                    </p>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            reverseButtons: true,
            focusCancel: true,
            width: 450,
        }).then((result) => {
            if (result.isConfirmed) {
                handelDeleteTask(checkCall?.taskId, checkCall?._id, checkCall?.scheduleAssignedTaskDetail?._id);
            }
        });
    };

    // ---------------- Add Task ----------------
    const [selectedTasks, setSelectedTasks] = useState([]);
    const [selectedCheckpoints, setSelectedCheckpoints] = useState([]);
    const [assignTaskModal, setAssignTaskModal] = useState(false);
    const tenantId = useSelector((state) => state?.userAuthReducer?.authenticatedUserDetail?.tenantId);
    const TaskModal = tenantId === "68b3c2e9603397c62fc2d835" ? TaskAssignmentModal : TaskAssignmentModalGeneric;

    useEffect(() => {
        if (addTask) {
            setSelectedTasks([]);
            setAssignTaskModal(true);
        }
    }, [addTask]);

    const [AssignNewScheduleTask, { loading: newScheduleTaskLoading }] = useMutation(AssignNewScheduleTaskBulkMutation);
    const [newScheduleTaskCheckCall, { loading: newScheduleTaskCheckCallLoading }] = useMutation(AddNewBulkTaskintoCheckCallMutation);

    const handelSubmit = async (tasksArg, checkpointsArg) => {
        const tasks = tasksArg ?? selectedTasks;
        const checkpoints = checkpointsArg ?? selectedCheckpoints;

        const allAssignInput = [
            ...tasks?.map((task) => ({
                taskId: task?.taskId,
                scheduleId,
                label: task?.label,
                priority: task?.priority,
                assignmentType: task?.assignmentType,
                from: task?.from,
                to: task?.to,
                location: task?.location,
                associatedSiteId: task?.associatedSiteId,
                associatedSiteGroupLocationId: task?.associatedSiteGroupLocationId,
            })),
            ...checkpoints?.map((cp) => ({
                checkpointId: cp?.checkpointId,
                scheduleId,
                label: cp?.label,
                priority: cp?.priority,
                assignmentType: cp?.assignmentType,
                from: cp?.from,
                to: cp?.to,
                location: cp?.location,
                associatedSiteGroupLocationId: cp?.associatedSiteGroupLocationId,
            })),
        ];

        if (allAssignInput.length === 0) return;

        const assignedTaskResponse = await AssignNewScheduleTask({ variables: { input: allAssignInput } });

        const allCheckCallInput = [
            ...tasks?.map((task, index) => ({
                siteId, scheduleId,
                taskId: task?.taskId,
                label: task?.label,
                scheduleAssignedTaskId: assignedTaskResponse?.data?.AssignNewScheduleTaskBulk?.response[index]?._id,
                priority: task?.priority,
                from: task?.from,
                to: task?.to,
                associatedSiteId: task?.associatedSiteId,
            })),
            ...checkpoints?.map((cp, cpIndex) => ({
                siteId, scheduleId,
                checkpointId: cp?.checkpointId,
                label: cp?.label,
                scheduleAssignedTaskId: assignedTaskResponse?.data?.AssignNewScheduleTaskBulk?.response[tasks.length + cpIndex]?._id,
                priority: cp?.priority,
                from: cp?.from,
                to: cp?.to,
                frequencyType: cp?.frequencyType,
                frequencyValue: cp?.frequencyValue,
            })),
        ];

        if (allCheckCallInput.length > 0) {
            await newScheduleTaskCheckCall({ variables: { newTaskCheckCall: allCheckCallInput } });
        }

        setAssignTaskModal(false);
        await refetch();
        dispatch(setNotification({ notificationState: true, error: false, message: "Task was added successfully!" }));
    };

    const taskList = calls.filter((call) => call?.callType === "Task");

    // ---------------- Table ----------------
    const hasCommentCapability = checkCallReportEnabled && calls.some((c) => c?.callType === "Check call");
    const hasImage = calls.some((c) =>
        (c?.callType === "Check call" && c?.checkCallActivityImage) ||
        ((c?.callType === "Clock In" || c?.callType === "Clock Out") && c?.selfie)
    );

    // Short values (type, times, status, ...) stay on one line - a squeezed column used to
    // split words mid-way ("Complet / ed"); only the free-text columns wrap, at word
    // boundaries, and past the modal's width the table scrolls sideways (scroll.x below).
    // With the fixed header (scroll.y) antd sizes each column from its body cells, so a
    // column whose cells are short or empty (Action is blank on most rows) came out
    // narrower than its own heading and clipped it ("ACTIO") - those get a width that
    // fits the heading.
    const nowrap = { className: "activity-nowrap" };
    const wrap = { className: "activity-wrap" };
    const columns = [
        { title: "Type", dataIndex: "typeTag", width: 120, ...nowrap },
        { title: "Label", dataIndex: "callLabel", width: 200, ...wrap },
        { title: "Priority", dataIndex: "priority", width: 110, ...nowrap },
        { title: "Expected Time", dataIndex: "expectedTime", ...nowrap },
        { title: "Actual Time", dataIndex: "actualTime", ...nowrap },
        { title: "Status", dataIndex: "status", width: 110, ...nowrap },
        { title: "Late", dataIndex: "late", width: 80, ...nowrap },
        { title: "Reason", dataIndex: "reason", width: 200, ...wrap },
        ...(hasCommentCapability ? [{ title: "Comment", dataIndex: "comment", width: 200, ...wrap }] : []),
        ...(hasImage ? [{ title: "Image", dataIndex: "image", width: 90, ...nowrap }] : []),
        { title: "Action", dataIndex: "action", width: 100, ...nowrap },
    ];

    // Flat, table-row-order list of every activity image (Clock In/Out selfie or Check
    // call image) - built alongside dataSource below so imagePreviewIndex above can index
    // straight into it, same as keOverview.jsx's `images` array for its Key Photos modal.
    const activityImages = [];

    const dataSource = [...calls]
        // Chronological (by requiredAt) in between, but Clock In is always the first row
        // and Clock Out always the last, regardless of what their own times say - a duty
        // starts and ends with those two no matter what.
        .sort((a, b) => {
            if (a?.callType === "Clock In") return -1;
            if (b?.callType === "Clock In") return 1;
            if (a?.callType === "Clock Out") return 1;
            if (b?.callType === "Clock Out") return -1;
            if (!a?.requiredAt) return 1;
            if (!b?.requiredAt) return -1;
            return new Date(a.requiredAt) - new Date(b.requiredAt);
        })
        .map((checkCall) => {
            const isTaskLike = checkCall?.callType === "Task" || checkCall?.callType === "Checkpoint";
            const typeStyle = TYPE_STYLES[checkCall?.callType] || DEFAULT_TYPE_STYLE;
            const imageSrc = checkCall?.checkCallActivityImage || checkCall?.selfie;
            const imageIndex = imageSrc ? activityImages.push(imageSrc) - 1 : null;

            return {
                key: checkCall?._id,
                typeTag: (
                    <Tag style={{ background: typeStyle.bg, color: typeStyle.color, border: "none", borderRadius: 6, fontWeight: 500 }}>
                        {checkCall?.callType}
                    </Tag>
                ),
                callLabel: isTaskLike ? (
                    <span style={{ textTransform: "capitalize" }} onClick={() => openDetail(checkCall)}>
                        <span className="link">{checkCall?.label}</span>
                        {checkCall?.associatedSiteDetail && (
                            <div style={{ color: "black" }}>
                                Associated with{" "}
                                <span
                                    className="link"
                                    onClick={(e) => { e.stopPropagation(); navigate("/user/site-detail/" + checkCall?.associatedSiteDetail?._id); }}
                                >
                                    {checkCall?.associatedSiteDetail?.sitename}
                                </span>{" "}
                                Site
                            </div>
                        )}
                    </span>
                ) : (
                    <span style={{ textTransform: "capitalize" }}>{checkCall?.label}</span>
                ),
                priority: checkCall?.priority != null ? getPriorityReverse(checkCall?.priority) : "--",
                expectedTime: checkCall?.requiredAt ? dayjs.utc(checkCall?.requiredAt).tz("Europe/London").format("DD/MM/YYYY HH:mm") : "--",
                actualTime: checkCall?.actualDate ? dayjs.utc(checkCall?.actualDate).tz("Europe/London").format("DD/MM/YYYY HH:mm") : "--",
                status: checkCall?.isCancelledByUser ? "Cancelled" : checkCall?.pending ? "Pending" : "Completed",
                late: checkCall?.late ? "Yes" : "No",
                reason: checkCall?.reason || "--",
                // Same thumbnail + click-to-preview pattern as Key Management's Key Photos
                // section (keOverview.jsx) - plain bordered <img> box, opens the shared
                // lightbox Modal below rather than antd's own Image preview chrome.
                image: imageSrc ? (
                    <div
                        onClick={() => setImagePreviewIndex(imageIndex)}
                        style={{
                            width: 50, height: 50, borderRadius: 6, overflow: "hidden",
                            border: "1px solid #f0f0f0", cursor: "pointer", flexShrink: 0,
                            background: "#f5f5f5",
                        }}
                    >
                        <img
                            src={imageSrc}
                            alt={`${checkCall?.callType || "activity"}-image`}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            onError={(e) => { e.target.style.display = "none"; }}
                        />
                    </div>
                ) : null,
                comment: (checkCall?.callType === "Check call" && checkCallReportEnabled) ? (
                    checkCall?.checkCallActivityComment ?
                        <div className="link" onClick={() => setCommentModal(checkCall)}>{checkCall?.checkCallActivityComment}</div> :
                        <button className="grid-sm-btn" style={{ fontSize: 9 }} onClick={() => setCommentModal(checkCall)}>Add Comment</button>
                ) : null,
                action: (isTaskLike && checkCall?.pending) ? (
                    <div style={{ color: "red", cursor: "pointer" }} onClick={() => confirmDelete(checkCall)}>Delete</div>
                ) : null,
            };
        });

    // Pagination pinned below the table instead of scrolling away with it - same
    // fixed-header/fixed-footer, scroll-only-the-rows layout the Employee/Site/Branch
    // list pages use (see components/table/index.jsx's table-pagination-bar), just done
    // locally here since this table lives inside a modal tab rather than a full page.
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    useEffect(() => { setCurrentPage(1); }, [dataSource.length]);
    const pagedDataSource = dataSource.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    // Table's scroll.y used to be a hardcoded "calc(100vh - 380px)", which only lines up
    // with the actual space left inside tableWrapperRef when the surrounding modal chrome
    // (header/tab bar/pagination) happens to be exactly the height that number assumed.
    // Any drift clips the bottom row against this container's overflow:hidden instead of
    // just scrolling it into view. Measuring the real rendered height (minus the table's
    // own header row) removes the guesswork so the last row is always reachable.
    const tableWrapperRef = useRef(null);
    const [tableScrollY, setTableScrollY] = useState(300);
    useEffect(() => {
        const wrapperEl = tableWrapperRef.current;
        if (!wrapperEl) return;

        const recalc = () => {
            const headerHeight = wrapperEl.querySelector(".ant-table-header")?.getBoundingClientRect().height || 0;
            setTableScrollY(Math.max(wrapperEl.clientHeight - headerHeight, 100));
        };

        recalc();
        const observer = new ResizeObserver(recalc);
        observer.observe(wrapperEl);
        const headerEl = wrapperEl.querySelector(".ant-table-header");
        if (headerEl) observer.observe(headerEl);

        return () => observer.disconnect();
    }, [columns.length]);

    // Secondary actions live behind one "Actions" menu instead of as separate buttons -
    // "See Activity on Map" stays its own standalone (filled) button since it's the
    // primary action here. Add Task only while the duty isn't completed yet (a completed
    // duty shouldn't be getting new tasks added).
    //
    // Download Check Call Report shows whenever the site has check-call reporting
    // switched on - even with nothing to report yet - but stays disabled (with a hover
    // explanation) until there's an actual "Check call" type row for this duty; other
    // types (Clock In/Out, Task, Checkpoint) don't count even though they ride along in
    // the PDF once it does unlock.
    const hasCheckCallType = calls.some((c) => c?.callType === "Check call");
    const reportDisabled = !hasCheckCallType || scheduleCheckCallLoading || scheduleCallData?.length === 0;
    const actionMenuItems = [
        ...(checkCallReportEnabled ? [{
            key: "download-report",
            disabled: reportDisabled,
            label: reportDisabled ? (
                <Tooltip title="No check calls recorded yet for this duty" placement="left">
                    <span>Download Check Call Report</span>
                </Tooltip>
            ) : (
                <PDFDownloadLink
                    document={<PatrolReportDocument data={scheduleCallData} />}
                    fileName={`${calls?.[0]?.siteDetail?.sitename}Guarding-report-${dayjs().toDate()}.pdf`}
                >
                    {({ loading }) => loading ? "Generating Check Call Report..." : "Download Check Call Report"}
                </PDFDownloadLink>
            ),
        }] : []),
        // the whole duty in one PDF (timeline, selfies, maps, every submitted report) -
        // separate from the check call report above
        {
            key: "download-full-report",
            disabled: !isCompleted,
            label: isCompleted ? "Download Full Duty Report" : (
                <Tooltip title="Available once the duty is completed" placement="left">
                    <span>Download Full Duty Report</span>
                </Tooltip>
            ),
        },
        ...(!isCompleted ? [{ key: "add-task", label: "Add Task" }] : []),
    ];
    const handleActionMenuClick = async ({ key }) => {
        if (key === "add-task") setAssignTaskModal(true);
        if (key === "download-full-report") {
            try {
                await downloadFullDutyReport(scheduleId);
            } catch (err) {
                dispatch(setNotification({ error: true, notificationState: true, message: err?.message || "Failed to download the duty report." }));
            }
        }
    };

    return (
        // Fixed header + fixed footer, only the rows scroll in between - same idea as
        // the Employee/Site/Branch list pages, sized to this modal rather than the
        // full page. Tune "100vh - Npx" below if the surrounding modal chrome
        // (header/tab bar) ever changes height - the table's own scroll.y is measured
        // at runtime (see tableWrapperRef above) so it doesn't need tuning here too.
        <div className="box-shadow border-radius" style={{ height: "calc(100vh - 260px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

            <div style={{ padding: "16px", flexShrink: 0 }}>
                <div className="hr-info-title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Activity

                    <div style={{ display: "flex", gap: "10px" }}>
                        

                        <button
                            className={mappableCalls.length === 0 ? "drawer-filled-btn disabled-btn" : "drawer-filled-btn"}
                            disabled={mappableCalls.length === 0}
                            onClick={() => setRouteModalOpen(true)}
                            style={{ padding: "8px 20px", display: "flex", alignItems: "center", gap: "8px" }}
                        >
                            <FontAwesomeIcon icon={faRoute} /> See Activity on Map
                        </button>

                        {actionMenuItems.length > 0 && (
                            <Dropdown overlay={<Menu items={actionMenuItems} onClick={handleActionMenuClick} />} trigger={["click"]}>
                                <button className="drawer-outlined-btn" style={{ padding: "8px 20px", display: "flex", alignItems: "center", gap: "8px" }}>
                                    Actions <CaretDownOutlined style={{ fontSize: "11px" }} />
                                </button>
                            </Dropdown>
                        )}
                    </div>
                </div>
            </div>

            <div ref={tableWrapperRef} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                <Table
                    columns={columns}
                    dataSource={pagedDataSource}
                    pagination={false}
                    scroll={{ x: "max-content", y: tableScrollY }}
                    className="activity-table"
                    loading={scheduleCheckCallLoading || newScheduleTaskLoading || newScheduleTaskCheckCallLoading || deleteAssignedTaskLoading}
                    locale={{ emptyText: "No activity recorded for this duty" }}
                />

                {Object.keys(commentModal)?.length > 0 && (
                    <CommentModal data={commentModal} close={() => setCommentModal({})} refetch={refetch} />
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "10px 0", flexShrink: 0, borderTop: "1px solid #ECEFEC" }}>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={dataSource.length}
                    showSizeChanger
                    onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
                />
            </div>

            {siteDutyTaskModal?.modal && (
                <SiteDutyTaskModal modalDetail={siteDutyTaskModal} close={() => setSiteDutyTaskModal(false)} refetch={refetch} />
            )}

            {formDetail?.modal && (
                <Modal open={true} closable={false} footer={null} width="100%" height="100vh" className=" editDutyModalWrapper" style={{ top: 0 }}>
                    <div className="modal-header-title">
                        <span style={{ letterSpacing: "0.2px" }}>{formDetail?.title}</span>
                        <span onClick={async () => { setFormDetail({}); }}><FontAwesomeIcon className="close" icon={faClose} /></span>
                    </div>

                    <EditDutyReportTab
                        templateId={formDetail?.templateId}
                        editData={formDetail?.editData}
                        jobData={formDetail?.jobData}
                        refetch={async () => { await refetch(); }}
                        refetchScheduleData={async () => { await refetch(); }}
                        height="100vh"
                        reportCallTypeId={formDetail?.reportCallTypeId}
                        isFromModal={true}
                        checkCallId={formDetail?.call?._id}
                    />
                </Modal>
            )}

            {assignTaskModal && (
                <TaskModal
                    visible={assignTaskModal}
                    close={() => setAssignTaskModal(false)}
                    selectedTasks={selectedTasks}
                    setSelectedTasks={setSelectedTasks}
                    selectedCheckpoints={selectedCheckpoints}
                    setSelectedCheckpoints={setSelectedCheckpoints}
                    checkpointDisabled={true}
                    standAlone={true}
                    handelSubmit={handelSubmit}
                    loading={newScheduleTaskCheckCallLoading || newScheduleTaskLoading}
                    sitegroupId={sitegroupId}
                    siteId={siteId}
                    shiftStartTime={shiftStartTime}
                    shiftEndTime={shiftEndTime}
                    associatedSiteGroupLocationId={taskList?.length > 0 ? taskList[0]?.associatedSiteGroupLocationId : null}
                />
            )}

            <RouteMapModal visible={routeModalOpen} coords={routeCoords} titles={routeTitles} onClose={() => setRouteModalOpen(false)} geofence={siteGeofence} />

            {/* Same transparent lightbox Key Management's Key Photos section uses (keOverview.jsx) */}
            <Modal
                open={imagePreviewIndex !== null}
                onCancel={() => setImagePreviewIndex(null)}
                footer={null}
                centered
                width="auto"
                styles={{ body: { padding: 0, textAlign: "center" }, content: { background: "transparent", boxShadow: "none" } }}
            >
                {imagePreviewIndex !== null && (
                    <div style={{ position: "relative", display: "inline-block" }}>
                        <img
                            src={activityImages[imagePreviewIndex]}
                            alt={`activity-preview-${imagePreviewIndex}`}
                            style={{ maxWidth: "80vw", maxHeight: "80vh", borderRadius: 8, display: "block" }}
                        />
                        {activityImages.length > 1 && (
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, gap: 8 }}>
                                <button
                                    onClick={() => setImagePreviewIndex((imagePreviewIndex - 1 + activityImages.length) % activityImages.length)}
                                    style={{ flex: 1, padding: "6px 16px", borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer", fontSize: 13 }}
                                >
                                    ← Prev
                                </button>
                                <span style={{ lineHeight: "32px", fontSize: 13, color: "#fff" }}>
                                    {imagePreviewIndex + 1} / {activityImages.length}
                                </span>
                                <button
                                    onClick={() => setImagePreviewIndex((imagePreviewIndex + 1) % activityImages.length)}
                                    style={{ flex: 1, padding: "6px 16px", borderRadius: 6, border: "1px solid #d9d9d9", background: "#fff", cursor: "pointer", fontSize: 13 }}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

        </div>
    );
};


const CommentModal = ({ data, close, refetch }) => {

    const dispatch = useDispatch();
    const [comment, setComment] = useState("");
    const [updateScheduleCheckCall, { loading: updateScheduleCheckCallLoading }] = useMutation(updateScheduleCheckCallMutation);

    useEffect(() => {
        if (data?.checkCallActivityComment) {
            setComment(data?.checkCallActivityComment);
        }
    }, [data]);

    const handelSave = async () => {
        try {
            await updateScheduleCheckCall({
                variables: {
                    input: {
                        _id: data?._id,
                        fields: { checkCallActivityComment: comment },
                    }
                }
            });

            dispatch(setNotification({ error: false, message: "Comment added Successfully", notificationState: true }));
            close();
            await refetch();
        } catch (err) {
            dispatch(setNotification({ error: true, message: "An Error encountered while adding comment", notificationState: true }));
            close();
            await refetch();
        }
    };

    return (
        <Modal
            open={true}
            width={450}
            footer={
                <div className={"drawer-footer"} style={{ padding: "12px 30px", paddingBottom: "16px", textAlign: "left", display: "flex", columnGap: "16px" }}>
                    <button
                        className={comment?.length == 0 ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"}
                        disabled={comment?.length == 0}
                        onClick={handelSave}
                    >
                        {updateScheduleCheckCallLoading ? <Spinner /> : "Save"}
                    </button>
                    <button disabled={false} className={"drawer-outlined-btn"} onClick={close}>
                        Cancel
                    </button>
                </div>
            }
            closable={false}
        >
            <div className={"modal-header-title"} style={{ height: "2.5em" }}>
                <span style={{ letterSpacing: "0.2px", textTransform: "capitalize" }}>Call Comments</span>
                <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>

            <div className="modal-body modal-body-padding">
                <Form.Item style={{ width: "100%", margin: "12px 0" }}>
                    <label>Call Comment</label>
                    <TextArea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="generic-input-control"
                        style={{ width: "100%" }}
                        placeholder="Add Comment"
                    />
                </Form.Item>
            </div>
        </Modal>
    );
};
